// VpnPathPanel.jsx - "VPN Path Analysis" card on Network Topology -> Tunnels & WAN.
// Data from backend/vpnPathAnalysis.js (GET /api/net/vpn-path). UI text in English.
import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, AlertTriangle, CheckCircle2, Activity, XCircle } from 'lucide-react';
import { api } from '../context/AuthContext';

const STATUS = {
  asymmetric: { label: 'Asymmetric', cls: 'bg-rose-50 text-rose-700 ring-rose-200', dot: '#e11d48' },
  symmetric: { label: 'Symmetric', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: '#10b981' },
  idle: { label: 'Idle', cls: 'bg-slate-100 text-slate-600 ring-slate-200', dot: '#94a3b8' },
  single: { label: 'Single VPN', cls: 'bg-slate-100 text-slate-600 ring-slate-200', dot: '#94a3b8' },
};
const RANGES = [[1, '24h'], [7, '7d'], [30, '30d']];
const COLORS = ['#2563eb', '#f59e0b', '#7c3aed', '#0d9488', '#db2777'];

const fmtBytes = (n) => {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB']; let i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(n >= 100 || i === 0 ? 0 : 1)} ${u[i]}`;
};
const fmtTime = (s) => (s ? new Date(s).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '-');
const fmtDur = (m) => (m < 60 ? `${m} min` : m < 1440 ? `${(m / 60).toFixed(1)} h` : `${(m / 1440).toFixed(1)} d`);
const pct = (x) => `${Math.round((x || 0) * 100)}%`;

function Badge({ status }) {
  const s = STATUS[status] || STATUS.idle;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ${s.cls}`}>
      <span className="w-2 h-2 rounded-full" style={{ background: s.dot }} />{s.label}
    </span>
  );
}

function ShareBar({ value, color }) {
  return (
    <div className="flex items-center gap-2 min-w-[110px]">
      <div className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: pct(value), background: color }} />
      </div>
      <span className="text-xs tabular-nums text-slate-600 w-9 text-right">{pct(value)}</span>
    </div>
  );
}

// Small multi-series line chart (bytes per period) for one direction.
function TrafficChart({ title, members, field }) {
  const W = 760, H = 120, P = 24;
  const all = members.flatMap((m) => m[field] || []);
  if (!all.length) return <div className="text-xs text-slate-400">{title}: no datapoints</div>;
  const t0 = Math.min(...all.map((x) => x[0])), t1 = Math.max(...all.map((x) => x[0]));
  const vmax = Math.max(1, ...all.map((x) => x[1]));
  const X = (t) => P + ((t - t0) / Math.max(1, t1 - t0)) * (W - 2 * P);
  const Y = (v) => H - P / 2 - (v / vmax) * (H - P);
  return (
    <div>
      <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
        <span className="font-medium">{title}</span><span>peak {fmtBytes(vmax)} / period</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-28">
        <line x1={P} x2={W - P} y1={H - P / 2} y2={H - P / 2} stroke="#e2e8f0" />
        {members.map((m, i) => {
          const s = m[field] || [];
          if (!s.length) return null;
          const d = s.map((p, k) => `${k ? 'L' : 'M'}${X(p[0]).toFixed(1)},${Y(p[1]).toFixed(1)}`).join(' ');
          return <path key={m.vpnId} d={d} fill="none" stroke={COLORS[i % COLORS.length]} strokeWidth="1.6" />;
        })}
        <text x={P} y={H - 2} fontSize="10" fill="#94a3b8">{fmtTime(new Date(t0).toISOString())}</text>
        <text x={W - P} y={H - 2} fontSize="10" fill="#94a3b8" textAnchor="end">{fmtTime(new Date(t1).toISOString())}</text>
      </svg>
    </div>
  );
}

function SiteCard({ site }) {
  const [showFlaps, setShowFlaps] = useState(false);
  const multi = site.members.length > 1;
  return (
    <div className={`rounded-2xl border bg-white p-4 ${site.status === 'asymmetric' ? 'border-rose-200' : 'border-slate-200'}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="font-semibold text-slate-800">{site.label}</div>
          <div className="text-xs text-slate-500">
            {site.device ? `${site.device} · ` : ''}{site.members.length} VPN connection{site.members.length > 1 ? 's' : ''}
            {multi && <> · active path in: <b>{site.activePath.in || '-'}</b> · out: <b>{site.activePath.out || '-'}</b></>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {site.flapCount > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 ring-1 ring-amber-200">
              <Activity size={12} /> {site.flapCount} flap{site.flapCount > 1 ? 's' : ''}
            </span>
          )}
          <Badge status={site.status} />
        </div>
      </div>

      {site.reasons.length > 0 && (
        <ul className="mt-3 space-y-1">
          {site.reasons.map((r, i) => (
            <li key={i} className="flex gap-2 text-sm text-rose-700"><XCircle size={15} className="mt-0.5 shrink-0" />{r}</li>
          ))}
        </ul>
      )}
      {site.warnings.map((w, i) => (
        <div key={i} className="mt-3 flex gap-2 rounded-xl bg-amber-50 ring-1 ring-amber-200 p-2.5 text-xs text-amber-800">
          <AlertTriangle size={15} className="shrink-0" /><span>{w}</span>
        </div>
      ))}

      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
              <th className="py-1.5 pr-3 font-medium">VPN</th>
              <th className="py-1.5 pr-3 font-medium">Customer gateway</th>
              <th className="py-1.5 pr-3 font-medium">Tunnels</th>
              <th className="py-1.5 pr-3 font-medium">Into AWS</th>
              {multi && <th className="py-1.5 pr-3 font-medium">In share</th>}
              <th className="py-1.5 pr-3 font-medium">From AWS</th>
              {multi && <th className="py-1.5 pr-3 font-medium">Out share</th>}
              <th className="py-1.5 pr-3 font-medium">Uptime</th>
              <th className="py-1.5 font-medium">Flaps</th>
            </tr>
          </thead>
          <tbody>
            {site.members.map((m, i) => (
              <tr key={m.vpnId} className="border-b border-slate-50 align-middle">
                <td className="py-1.5 pr-3">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="font-medium text-slate-800">{m.name}</span>
                    {m.oneWay && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 ring-1 ring-rose-200">{m.oneWay === 'in_only' ? 'one-way in' : 'one-way out'}</span>}
                  </div>
                  <div className="text-[11px] text-slate-400">{m.vpnId}</div>
                </td>
                <td className="py-1.5 pr-3 tabular-nums text-slate-600">{m.customerGatewayIp || '-'}</td>
                <td className="py-1.5 pr-3">
                  <span className={m.tunnelsUp === m.tunnels.length && m.tunnels.length ? 'text-emerald-700' : 'text-rose-700'}>{m.tunnelsUp}/{m.tunnels.length} UP</span>
                </td>
                <td className="py-1.5 pr-3 tabular-nums">{fmtBytes(m.bytesIn)}</td>
                {multi && <td className="py-1.5 pr-3"><ShareBar value={m.inShare} color={COLORS[i % COLORS.length]} /></td>}
                <td className="py-1.5 pr-3 tabular-nums">{fmtBytes(m.bytesOut)}</td>
                {multi && <td className="py-1.5 pr-3"><ShareBar value={m.outShare} color={COLORS[i % COLORS.length]} /></td>}
                <td className="py-1.5 pr-3 tabular-nums">{m.uptimePct == null ? '-' : `${m.uptimePct}%`}</td>
                <td className="py-1.5 tabular-nums">{m.flaps.length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {multi && (
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <TrafficChart title="Into AWS (TunnelDataIn)" members={site.members} field="seriesIn" />
          <TrafficChart title="From AWS (TunnelDataOut)" members={site.members} field="seriesOut" />
        </div>
      )}

      {site.overlaps.length > 0 && (
        <div className="mt-3 text-xs text-slate-600">
          <span className="font-medium text-slate-700">Prefixes on more than one VPN: </span>
          {site.overlaps.map((o) => o.cidr).join(', ')}
          {site.exclusive.length > 0 && <> · <span className="font-medium text-slate-700">single-VPN: </span>{site.exclusive.map((o) => `${o.cidr} (${o.vpn})`).join(', ')}</>}
        </div>
      )}

      {site.flaps.length > 0 && (
        <div className="mt-3">
          <button onClick={() => setShowFlaps((v) => !v)} className="text-xs font-medium text-blue-700 hover:underline">
            {showFlaps ? 'Hide' : 'Show'} tunnel outage history ({site.flaps.length})
          </button>
          {showFlaps && (
            <table className="mt-2 w-full text-xs">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-100">
                  <th className="py-1 pr-3 font-medium">VPN</th><th className="py-1 pr-3 font-medium">Start</th>
                  <th className="py-1 pr-3 font-medium">End</th><th className="py-1 pr-3 font-medium">Duration</th><th className="py-1 font-medium">Severity</th>
                </tr>
              </thead>
              <tbody>
                {site.flaps.map((f, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-1 pr-3">{f.vpn}</td><td className="py-1 pr-3 tabular-nums">{fmtTime(f.start)}</td>
                    <td className="py-1 pr-3 tabular-nums">{f.ongoing ? 'ongoing' : fmtTime(f.end)}</td>
                    <td className="py-1 pr-3 tabular-nums">{fmtDur(f.durationMin)}</td>
                    <td className="py-1">{f.severity === 'all_down' ? <span className="text-rose-700">All tunnels down</span> : <span className="text-amber-700">One tunnel down</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export default function VpnPathPanel() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSingle, setShowSingle] = useState(false);

  const load = useCallback(async (refresh = false) => {
    setLoading(true); setErr('');
    try {
      const r = await api.get('/api/net/vpn-path', { params: { days, ...(refresh ? { refresh: 1 } : {}) } });
      setData(r.data);
    } catch (e) {
      setErr((e.response && e.response.data && e.response.data.error) || e.message);
    } finally { setLoading(false); }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const sites = (data && data.sites) || [];
  const multi = sites.filter((s) => s.members.length > 1);
  const single = sites.filter((s) => s.members.length <= 1);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Activity size={18} className="text-slate-600" />
          <div>
            <div className="font-semibold text-slate-800">VPN Path Analysis · AWS Site-to-Site VPN</div>
            <div className="text-xs text-slate-500">
              {data ? `${data.account.label || 'AWS'}${data.account.accountId ? ` (${data.account.accountId})` : ''} · ${data.region} · ` : ''}
              Traffic direction per VPN (CloudWatch TunnelDataIn/Out), route overlap and tunnel outages
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {RANGES.map(([d, l]) => (
            <button key={d} onClick={() => setDays(d)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium ${days === d ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{l}</button>
          ))}
          <button onClick={() => load(true)} disabled={loading}
            className="ml-1 inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-600 text-white disabled:opacity-60">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {err && <div className="mt-3 text-sm text-rose-700">Failed to load: {err}</div>}
      {!data && !err && <div className="mt-3 text-sm text-slate-500">Loading CloudWatch metrics…</div>}

      {data && (
        <>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ['VPN connections', data.summary.vpnCount],
              ['Multi-VPN sites', data.summary.multiVpnSites],
              ['Asymmetric sites', data.summary.asymmetric],
              [`Tunnel outages (${days === 1 ? '24h' : `${days}d`})`, data.summary.flaps],
            ].map(([l, v]) => (
              <div key={l} className="rounded-2xl border border-slate-200 p-3">
                <div className="text-xs text-slate-500">{l}</div>
                <div className={`mt-1 text-xl font-semibold tabular-nums ${l.startsWith('Asym') && v ? 'text-rose-700' : 'text-slate-800'}`}>{v}</div>
              </div>
            ))}
          </div>

          <div className="mt-3 space-y-3">
            {multi.map((s) => <SiteCard key={s.key} site={s} />)}
            {!multi.length && (
              <div className="flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 size={16} /> No site uses more than one VPN connection.</div>
            )}
          </div>

          {single.length > 0 && (
            <div className="mt-3">
              <button onClick={() => setShowSingle((v) => !v)} className="text-xs font-medium text-blue-700 hover:underline">
                {showSingle ? 'Hide' : 'Show'} single-VPN connections ({single.length})
              </button>
              {showSingle && <div className="mt-2 space-y-3">{single.map((s) => <SiteCard key={s.key} site={s} />)}</div>}
            </div>
          )}
          <div className="mt-3 text-[11px] text-slate-400">
            Generated {fmtTime(data.generatedAt)}{data.cached ? ' (cached, 5 min)' : ''} · "Into AWS" = TunnelDataIn, "From AWS" = TunnelDataOut.
            A site is flagged asymmetric when traffic enters AWS on one VPN but returns on another, or when one VPN carries traffic in one direction only.
          </div>
        </>
      )}
    </div>
  );
}
